import { useState, useEffect, useRef } from 'react';
import { useLayeredHistory } from '@/hooks/use-layered-history';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

import { 
  ShoppingBasket, 
  Receipt, 
  MoreVertical, 
  ChevronLeft, 
  ChevronRight,
  UserCheck,
  Calendar,
  Layers,
  CheckCircle,
  XCircle,
  Clock,
  Edit3,
  Trash2,
  Image as ImageIcon,
  ZoomIn,
  X,
  Search,
  Copy,
  Check,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import DateInput from '@/components/ui/DateInput';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from '@/components/ui/dialog';
import { ImageLightbox } from '@/components/ui/ImageLightbox';
import { Expense, User, GroupMember } from '../types';
import { formatAmount } from '@/lib/utils';
import { useToast } from '../hooks/use-toast';

export default function ExpenseList({ store, setView, isSearchView = false, monthFilter, onClearMonthFilter }: { store: any, setView: (v: any) => void, isSearchView?: boolean, monthFilter?: { year: number; month: number } | null, onClearMonthFilter?: () => void }) {
  const { expenses, users, currentUser, groupMembers, deleteExpense, setEditingExpense } = store;
  const { showToast } = useToast();
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [selectedProfileUser, setSelectedProfileUser] = useState<User | null>(null);
  const [profileAvatarImgError, setProfileAvatarImgError] = useState(false);

  // Auto-open expense from URL on initial load (e.g. /expenses/:id)
  // 在 component 初始化時就抓好 URL 裡的 ID，避免後面 URL 同步 effect 覆蓋掉
  const initialExpenseIdRef = useRef<string | null>(
    typeof window !== 'undefined'
      ? (window.location.pathname.match(/^\/expenses\/(.+)/)?.[1] ?? null)
      : null
  );
  const didAutoOpen = useRef(false);
  // 追蹤是否因使用者點擊而 pushState（非深層連結）
  const didPushHistoryRef = useRef(false);

  const { pushLayer, popLayer, historyLayerRef } = useLayeredHistory((layer) => {
    if (layer === 'zoomedImage') {
      setZoomedImage(null);
    } else if (layer === 'profileUser') {
      setSelectedProfileUser(null);
    } else {
      // expense layer or deep-link
      didPushHistoryRef.current = false;
      setSelectedExpense(null);
    }
  });

  useEffect(() => {
    if (didAutoOpen.current || !expenses.length) return;
    const targetId = initialExpenseIdRef.current;
    if (!targetId) { didAutoOpen.current = true; return; }
    const found = expenses.find((e: Expense) => String(e.id) === targetId);
    if (found) {
      setSelectedExpense(found);
      didAutoOpen.current = true;
    }
  }, [expenses]);

  const handleShare = () => {
    if (typeof window === 'undefined') return;
    navigator.clipboard.writeText(window.location.href).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Dialog 開啟時 pushState 新增歷史，讓上一頁可以關閉 dialog
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (selectedExpense) {
      const targetPath = `/expenses/${selectedExpense.id}`;
      if (window.location.pathname !== targetPath) {
        window.history.pushState(null, '', targetPath);
        historyLayerRef.current = ['expense'];
        didPushHistoryRef.current = true;
      }
    }
  }, [selectedExpense]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedParticipantSettlement, setSelectedParticipantSettlement] = useState<any | null>(null);
  
  // Date filter state
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [finalSearchTerm, setFinalSearchTerm] = useState('');


  
  const handleSearchTrigger = () => {
    setFinalSearchTerm(searchTerm);
  };

  const visibleExpenses = expenses.filter((exp: Expense) => {
    if (!currentUser) return false;
    const isParticipant = exp.payerId === currentUser.id || exp.participants.some(p => p.userId === currentUser.id);
    if (!isParticipant) return false;

    // Month filter from chart click
    if (monthFilter) {
      const rawDate = exp.expenseDate.slice(0, 10);
      const [ey, em] = rawDate.split('-').map(Number);
      if (ey !== monthFilter.year || em !== monthFilter.month) return false;
    }
    
    // Keyword search
    const term = isSearchView ? finalSearchTerm : searchTerm;
    if (term && !exp.title.toLowerCase().includes(term.toLowerCase())) return false;
    
    // Date range filter
    if (isSearchView) {
      const expDate = exp.expenseDate.split('T')[0];
      if (startDate && expDate < startDate) return false;
      if (endDate && expDate > endDate) return false;
    }

    return true;
  });

  const getUserShare = (exp: Expense) => {
    const participant = exp.participants.find(p => p.userId === currentUser?.id);
    return participant ? participant.amountDue : 0;
  };

  // 年度累計（當年所有有參與的支出）
  const nowForTotal = new Date();
  const thisYearForTotal = nowForTotal.getFullYear();
  const thisMonthForTotal = nowForTotal.getMonth() + 1;
  const yearlyTotal = expenses.reduce((sum: number, exp: Expense) => {
    const rawDate = exp.expenseDate.slice(0, 10);
    const [ey] = rawDate.split('-').map(Number);
    if (ey !== thisYearForTotal) return sum;
    const myPart = exp.participants.find((p: any) => p.userId === currentUser?.id);
    return sum + (myPart?.amountDue ?? 0);
  }, 0);
  // 本月累計
  const monthlyTotal = expenses.reduce((sum: number, exp: Expense) => {
    const rawDate = exp.expenseDate.slice(0, 10);
    const [ey, em] = rawDate.split('-').map(Number);
    if (ey !== thisYearForTotal || em !== thisMonthForTotal) return sum;
    const myPart = exp.participants.find((p: any) => p.userId === currentUser?.id);
    return sum + (myPart?.amountDue ?? 0);
  }, 0);

  const userShareTotal = visibleExpenses.reduce((acc: number, exp: Expense) => acc + getUserShare(exp), 0);
  void userShareTotal; // kept for dialog detail usage
  const isItemizedExpense = !!selectedExpense?.items && selectedExpense.items.length > 0;

  const sortedItemizedItems = (selectedExpense?.items || []).slice().sort((a: any, b: any) => {
    const aIsSelf = a.debtorId === currentUser?.id;
    const bIsSelf = b.debtorId === currentUser?.id;
    if (aIsSelf && !bIsSelf) return -1;
    if (!aIsSelf && bIsSelf) return 1;
    return 0;
  });

  const closeExpenseDialog = () => {
    if (typeof window !== 'undefined') {
      if (didPushHistoryRef.current) {
        didPushHistoryRef.current = false;
        historyLayerRef.current = [];
        window.history.back();
      } else if (window.location.pathname.startsWith('/expenses/')) {
        window.history.replaceState(null, '', '/expenses');
      }
    }
    setSelectedExpense(null);
  };

  const handleDelete = async (id: string) => {
    const target = expenses.find((e: Expense) => e.id === id);
    const hasSettledOthers = !!target?.participants.some(
      (p: Expense['participants'][number]) => p.userId !== target.payerId && p.isPaid && p.amountDue > 0,
    );
    const confirmMsg = hasSettledOthers
      ? '此筆支出已有人結算。刪除後系統會自動建立「取消退還」待結算，請向對方追討／退還已轉帳金額。確定刪除？'
      : '確定要刪除這筆支出紀錄嗎？';
    if (confirm(confirmMsg)) {
      try {
        await deleteExpense(id);
        closeExpenseDialog();
      } catch (error) {
        showToast('刪除失敗', 'error');
      }
    }
  };

  return (
    <div className="space-y-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
        <div>
          <h1 className="text-3xl md:text-5xl font-black tracking-tight text-primary mb-2">支出紀錄</h1>
          <p className="text-xs md:text-base text-muted-foreground max-w-md font-medium">完整記錄家庭協作支出與貢獻的封存檔案。</p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <Card className="col-span-12 md:col-span-4 bg-muted/20 p-6 md:p-8 rounded-2xl md:rounded-[2rem] flex justify-between items-center border border-border/50">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 block">{thisYearForTotal}年度總支出</span>
            <div className="text-3xl md:text-5xl font-black text-primary">
              ${formatAmount(yearlyTotal)}
            </div>
          </div>
          <div className="flex -space-x-2 md:-space-x-3">
             {groupMembers.slice(0, 5).map((gm: GroupMember) => {
               const u = users.find((u: User) => u.id === gm.userId);
               return (
                <Avatar
                  key={`${gm.userId}-${gm.avatarUrl}`}
                  className="w-8 h-8 md:w-12 md:h-12 rounded-full border-2 md:border-4 border-background overflow-hidden cursor-pointer hover:scale-110 hover:z-10 transition-transform"
                  title={gm.nickname || '未知'}
                  onClick={() => { if (u) { setProfileAvatarImgError(false); setSelectedProfileUser(u); pushLayer('profileUser'); } }}
                >
                  <AvatarImage src={gm.avatarUrl ?? undefined} className="object-cover" />
                  <AvatarFallback className="text-xs font-bold bg-muted text-muted-foreground">
                    {gm.nickname?.[0] || '？'}
                  </AvatarFallback>
                </Avatar>
               );
             })}
             {groupMembers.length > 5 && (
                <div className="w-8 h-8 md:w-12 md:h-12 rounded-full border-2 md:border-4 border-background bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">+{groupMembers.length - 5}</div>
             )}
          </div>
        </Card>
        <Card className="col-span-12 md:col-span-4 bg-muted/20 p-6 md:p-8 rounded-2xl md:rounded-[2rem] border border-border/50">
          <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 block">{thisYearForTotal}年{thisMonthForTotal}月本月支出</span>
          <div className="text-3xl md:text-5xl font-black text-primary">
            ${formatAmount(monthlyTotal)}
          </div>
        </Card>
        <Card className="col-span-12 md:col-span-4 bg-gradient-to-br from-primary to-accent p-6 md:p-8 rounded-2xl md:rounded-[2rem] text-primary-foreground shadow-wash">
          <span className="text-xs font-bold uppercase tracking-widest opacity-70 mb-2 block">活躍支出</span>
          <div className="text-3xl md:text-5xl font-black mb-2">
            {visibleExpenses.filter((e: Expense) => !e.participants.every(p => p.isPaid)).length}
          </div>
          <p className="text-xs md:text-sm opacity-90 font-medium">尚有成員未結清之帳目。</p>
        </Card>
      </div>

      <Card className="bg-muted/10 rounded-2xl md:rounded-[2rem] shadow-wash overflow-hidden border border-border/50">
        <div className="p-6 md:p-8 border-b border-border/50 flex flex-col gap-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <h3 className="text-lg md:text-xl font-bold text-primary text-center md:text-left whitespace-nowrap">
              {isSearchView ? '搜尋結果' : monthFilter ? `${monthFilter.year}年${monthFilter.month}月 支出紀錄` : '交易歷史'}
            </h3>
            <div className="flex items-center gap-2">
              {monthFilter && !isSearchView && (
                <button
                  onClick={onClearMonthFilter}
                  className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                >
                  <X size={12} />
                  清除筛選
                </button>
              )}
              <div className="relative w-full md:max-w-xs flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                <Input 
                  placeholder="搜尋項目名稱..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') isSearchView ? handleSearchTrigger() : null;
                  }}
                  className="pl-10 h-10 rounded-xl bg-background border-border/50 focus:ring-primary/20"
                />
              </div>
              {isSearchView && (
                <Button onClick={handleSearchTrigger} className="rounded-xl h-10 px-4 font-black text-xs">
                  搜尋
                </Button>
              )}
              </div>
            </div>
          </div>
          
          {isSearchView && (
            <div className="flex flex-wrap items-center gap-4 p-4 bg-muted/30 rounded-2xl border border-border/40">
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest">日期區間</span>
                <div className="flex items-center gap-2">
                   <DateInput
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    wrapperClassName="w-36"
                    className="h-9 rounded-l-lg bg-background border-border/50 text-xs"
                   />
                   <span className="text-muted-foreground">~</span>
                   <DateInput
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    wrapperClassName="w-36"
                    className="h-9 rounded-l-lg bg-background border-border/50 text-xs"
                   />
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="divide-y divide-border/20 p-4 space-y-4">
          {visibleExpenses.length > 0 ? (
            visibleExpenses.map((exp: Expense) => {
              const allPaid = exp.participants.every(p => p.isPaid);
              return (
                <div 
                  key={exp.id}
                  onClick={() => setSelectedExpense(exp)}
                  className="group flex flex-col md:flex-row items-center justify-between p-4 hover:bg-muted/50 rounded-2xl transition-all cursor-pointer border border-transparent hover:border-primary/20 gap-4"
                >
                  <div className="flex items-center gap-6 flex-1 w-full">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors overflow-hidden">
                      {exp.imageUrl ? (
                        <img src={exp.imageUrl} alt={exp.title} className="w-full h-full object-cover" />
                      ) : (
                        <ShoppingBasket size={20} />
                      )}
                    </div>
                    <div>
                      <h4 className="font-bold text-foreground">{exp.title}</h4>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-muted-foreground flex items-center gap-1 leading-none">
                          <Calendar size={12} />
                          {new Date(exp.expenseDate).toLocaleDateString()}
                        </span>
                        <span className="w-1 h-1 rounded-full bg-border"></span>
                        <div className="flex items-center gap-1">
                          <Badge variant="outline" className="text-[10px] h-5 bg-background border-primary/30 text-primary font-black px-1.5 flex items-center gap-1">
                            <span className="opacity-60 font-medium">由</span>
                            {users.find((u: User) => u.id === exp.payerId)?.nickname || '未知'}
                            <span className="opacity-60 font-medium ml-0.5">支付</span>
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-12 w-full md:w-auto justify-between md:justify-end">
                    <div className="text-right">
                      <div className="text-xl font-black text-foreground">${formatAmount(exp.amount)}</div>
                      <Badge variant="secondary" className={`text-[10px] font-bold rounded-full uppercase ${allPaid ? 'bg-secondary/20 text-secondary' : 'bg-primary/20 text-primary'}`}>
                        {allPaid ? '已結清' : '結清中'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex -space-x-2 mr-2">
                         {exp.participants.map(p => {
                            const u = (p as any).user || users.find((user: User) => user.id === p.userId);
                            const avatarSrc = u?.avatarUrl;
                            return (
                              <Avatar key={`${p.userId}-${avatarSrc}`} className={`w-8 h-8 rounded-full border-2 border-background overflow-hidden ${p.isPaid ? 'bg-secondary' : 'bg-muted'}`}>
                                <AvatarImage src={avatarSrc} className="object-cover" />
                                <AvatarFallback className={`text-[8px] font-bold ${p.isPaid ? 'text-secondary-foreground' : 'text-muted-foreground'}`}>
                                   {p.isPaid ? <UserCheck size={10} /> : u?.nickname?.[0] || '？'}
                                </AvatarFallback>
                              </Avatar>
                            );
                         })}
                      </div>
                      <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-primary hover:bg-muted rounded-xl">
                        <ChevronRight size={20} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="p-20 text-center text-muted-foreground">
              尚無交易紀錄
            </div>
          )}
        </div>

        <div className="p-6 bg-muted/20 flex items-center justify-between border-t border-border/50">
          <span className="text-xs text-muted-foreground font-medium">顯示共 {visibleExpenses.length} 筆交易</span>
        </div>
      </Card>

      {/* Detail Modal */}
      <Dialog open={!!selectedExpense} onOpenChange={(open) => !open && closeExpenseDialog()}>
        <DialogContent className="bg-muted border-border rounded-[2rem] p-0 max-w-[min(95vw,1100px)] shadow-wash max-h-[90dvh] flex flex-col overflow-hidden">
          {/* Scrollable body */}
          <div className="overflow-y-auto overscroll-contain flex-1 no-scrollbar p-4 sm:p-6 md:p-8">
          <DialogHeader>
            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4 md:mb-6">
              <Receipt size={32} />
            </div>
            <DialogTitle className="text-2xl md:text-3xl font-black text-primary break-words">{selectedExpense?.title}</DialogTitle>
            <DialogDescription className="text-muted-foreground font-medium pt-2">
              詳細支出資訊與各成員支付狀態。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 md:space-y-8 my-4 md:my-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              <div className="bg-background/50 p-4 rounded-2xl border border-border/50">
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest block mb-1">交易金額</span>
                <div className="text-2xl font-black text-foreground">${selectedExpense?.amount.toLocaleString()}</div>
              </div>
              <div className="bg-background/50 p-4 rounded-2xl border border-border/50">
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest block mb-1">交易日期</span>
                <div className="text-sm font-bold flex items-center gap-2 mt-2">
                  <Calendar size={16} className="text-primary" />
                  {selectedExpense && new Date(selectedExpense.expenseDate).toLocaleDateString()}
                </div>
              </div>
            </div>

            {!isItemizedExpense && (
              <div>
                <h4 className="text-sm font-bold text-primary mb-4 flex items-center gap-2 uppercase tracking-tighter">
                  <Layers size={16} /> 分帳參與人員 ({selectedExpense?.participants.length})
                </h4>
                <div className="space-y-3">
                  {selectedExpense?.participants.map(p => {
                    const participantUser = (p as any).user || users.find((u: User) => u.id === p.userId);
                    const isPayer = selectedExpense.payerId === p.userId;
                    return (
                      <div 
                        key={p.userId} 
                        className={`flex flex-col p-4 bg-background/30 rounded-2xl border transition-all ${p.isPaid && p.settlement ? 'cursor-pointer hover:border-primary/50' : 'border-border/20'}`}
                        onClick={() => {
                          if (p.isPaid && p.settlement) {
                            setSelectedParticipantSettlement(p);
                          }
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                          <div className="relative">
                            <Avatar 
                              className="w-10 h-10 rounded-xl overflow-hidden border border-border/30"
                              key={`detail-${p.userId}-${participantUser?.avatarUrl}`}
                            >
                              <AvatarImage src={participantUser?.avatarUrl} className="object-cover" />
                              <AvatarFallback className="bg-muted text-sm font-bold">
                                {participantUser?.nickname?.[0] || '？'}
                              </AvatarFallback>
                            </Avatar>
                          </div>
                            <div>
                              <p className="text-sm font-bold flex items-center gap-2">
                                {participantUser?.nickname}
                                {isPayer && <Badge variant="outline" className="text-[8px] h-4 bg-primary/10 text-primary border-primary/20">發起人</Badge>}
                              </p>
                              <p className="text-xs text-muted-foreground font-medium">應付金額: ${p.amountDue.toLocaleString()}</p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end">
                            {p.isPaid ? (
                              <div className="flex items-center gap-1 text-secondary font-bold text-xs">
                                <CheckCircle size={14} /> 已支付
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-destructive font-bold text-xs">
                                <XCircle size={14} /> 未支付
                              </div>
                            )}
                            {p.isPaid && p.settlement && (
                              <p className="text-[10px] text-primary font-bold mt-1 flex items-center gap-1">
                                查看收據 <ChevronRight size={10} />
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Itemized List (if exists) */}
            {selectedExpense?.items && selectedExpense.items.length > 0 && (
              <div className="mt-8 border-t border-border/20 pt-6">
                <h4 className="text-sm font-bold text-primary mb-4 flex items-center gap-2 uppercase tracking-tighter">
                  <Receipt size={16} /> 明細清單 ({selectedExpense.items.length})
                </h4>
                <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                  {sortedItemizedItems.map((item, idx) => (
                    <div key={item.id || idx} className="flex items-center justify-between text-xs py-3 px-4 bg-background/30 rounded-xl border border-border/10">
                      <span className="font-bold text-foreground flex-1">{item.name || `項目 ${idx + 1}`}</span>
                      <span className="text-muted-foreground w-24 text-center">{users.find((u: User) => u.id === item.debtorId)?.nickname || '-'}</span>
                      <span className="font-black text-primary w-20 text-right">${item.amount.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Receipt Image (if exists) */}
            {selectedExpense?.imageUrl && (
              <div className="mt-8 border-t border-border/20 pt-6">
                 <h4 className="text-sm font-bold text-primary mb-4 flex items-center gap-2 uppercase tracking-tighter">
                  <ImageIcon size={16} /> 收據相片
                </h4>
                <div 
                  className="relative rounded-2xl overflow-hidden cursor-zoom-in group border-2 border-border/40 hover:border-primary/50 transition-colors min-h-[180px] flex items-center justify-center bg-black/10"
                  onClick={() => { setZoomedImage(selectedExpense.imageUrl!); pushLayer('zoomedImage'); }}
                >
                   <img src={selectedExpense.imageUrl} alt="Receipt" className="w-full h-auto min-h-[100px] max-h-[300px] object-contain bg-black/20" />
                   <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="p-3 bg-white/20 backdrop-blur-md rounded-full text-white">
                        <ZoomIn size={24} />
                      </div>
                   </div>
                </div>
              </div>
            )}
          </div>
          </div>{/* end scrollable body */}

          {/* Action buttons — fixed at bottom, outside scroll area */}
          <div className="shrink-0 px-4 sm:px-6 md:px-8 pb-4 sm:pb-6 md:pb-8 pt-3 border-t border-border/20 space-y-3">
             {/* Owner Actions */}
             {selectedExpense?.payerId === currentUser?.id && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button 
                  variant="outline" 
                  className="h-12 sm:h-14 rounded-2xl border-primary/20 text-primary font-black flex items-center gap-2 hover:bg-primary/5 shadow-sm"
                  onClick={() => {
                    setEditingExpense(selectedExpense);
                    setView('edit-expense');
                  }}
                >
                  <Edit3 size={18} /> 修改紀錄
                </Button>
                <Button 
                  variant="ghost" 
                  className="h-12 sm:h-14 rounded-2xl text-destructive font-black flex items-center gap-2 hover:bg-destructive/10"
                  onClick={() => handleDelete(selectedExpense.id)}
                >
                  <Trash2 size={18} /> 刪除紀錄
                </Button>
              </div>
            )}

            <button
              onClick={handleShare}
              className="w-full flex items-center justify-center gap-2 h-10 rounded-2xl font-black text-xs border border-border/30 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
            >
              {copied ? <Check size={15} className="text-secondary" /> : <Copy size={15} />}
              {copied ? '已複製連結' : '分享此頁面'}
            </button>
            <Button 
              className="w-full h-12 sm:h-14 rounded-2xl font-black text-base sm:text-lg shadow-md"
              onClick={() => setSelectedExpense(null)}
            >
              關閉詳細內容
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Image Lightbox */}
      {zoomedImage && (
        <ImageLightbox src={zoomedImage} onClose={() => { setZoomedImage(null); popLayer(); }} />
      )}

      {/* Participant Settlement Detail Modal */}
      <Dialog open={!!selectedParticipantSettlement} onOpenChange={(open) => !open && setSelectedParticipantSettlement(null)}>
        <DialogContent className="bg-muted border-border rounded-[2.5rem] p-8 max-w-lg shadow-wash">
          <DialogHeader>
            <div className="w-14 h-14 rounded-2xl bg-secondary/10 text-secondary flex items-center justify-center mb-4">
              <CheckCircle size={28} />
            </div>
            <DialogTitle className="text-2xl font-black text-primary">付款詳細資訊</DialogTitle>
            <DialogDescription className="text-muted-foreground font-medium pt-1">
              查看該成員的支付證明與時間。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 my-6">
            <div className="bg-background/50 p-6 rounded-2xl border border-border/50 flex items-center justify-between">
              <div>
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest block mb-1">支付成員</span>
                <div className="text-lg font-black text-foreground">{users.find((u: User) => u.id === selectedParticipantSettlement?.userId)?.nickname}</div>
              </div>
              <div className="text-right">
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest block mb-1">支付時間</span>
                <div className="text-xs font-medium flex items-center gap-1">
                  <Clock size={12} /> {selectedParticipantSettlement?.settlement && new Date(selectedParticipantSettlement.settlement.createdAt).toLocaleString()}
                </div>
              </div>
            </div>

            {selectedParticipantSettlement?.settlement?.receiptUrl && (
              <div className="space-y-3">
                <h5 className="text-xs font-bold text-muted-foreground uppercase tracking-widest ml-1 flex items-center gap-2">
                  <ImageIcon size={12} /> 付款證明
                </h5>
                <div 
                  className="relative rounded-2xl overflow-hidden cursor-zoom-in group border-2 border-border/40 hover:border-primary/50 transition-colors"
                  onClick={() => { setZoomedImage(selectedParticipantSettlement.settlement.receiptUrl!); pushLayer('zoomedImage'); }}
                >
                  <img src={selectedParticipantSettlement.settlement.receiptUrl} alt="Receipt" className="w-full h-auto max-h-[240px] object-contain bg-black/20" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="p-3 bg-white/20 backdrop-blur-md rounded-full text-white">
                      <ZoomIn size={24} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <Button 
            className="w-full h-14 rounded-2xl font-black text-lg shadow-md"
            onClick={() => setSelectedParticipantSettlement(null)}
          >
            返回
          </Button>
        </DialogContent>
      </Dialog>

      {/* Profile Avatar Modal */}
      <Dialog open={!!selectedProfileUser} onOpenChange={(open) => { if (!open) { setSelectedProfileUser(null); popLayer(); } else setProfileAvatarImgError(false); }}>
        <DialogContent className="max-w-md p-0 border-none bg-transparent shadow-none flex flex-col items-center" showCloseButton={false}>
          <div className="relative group p-4 w-full">
            <div className="relative aspect-square w-full rounded-[3rem] overflow-hidden shadow-2xl border-4 border-white/10 bg-muted">
              {selectedProfileUser?.avatarUrl && !profileAvatarImgError ? (
                <img
                  src={selectedProfileUser.avatarUrl}
                  className="w-full h-full object-cover"
                  onError={() => setProfileAvatarImgError(true)}
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-8xl font-black text-muted-foreground/20">
                  {selectedProfileUser?.nickname?.[0]}
                </div>
              )}
            </div>
            <div className="mt-6 text-center text-white">
              <h3 className="text-2xl font-black">{selectedProfileUser?.nickname}</h3>
              <p className="text-sm font-medium opacity-60">{selectedProfileUser?.email}</p>
            </div>
            <button
              onClick={() => { setSelectedProfileUser(null); popLayer(); }}
              className="absolute -top-2 -right-2 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-md transition-all shadow-xl"
            >
              <X size={24} />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
